// src/components/SignatureForm.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Button, Badge, Alert } from './ui';
import { api, endpoints } from '../config/api';
import {
  PERSONAL_PLACEHOLDERS, PERSONALIZATION_CHIPS, SURVEY_SCALES, SURVEY_OPTIONS, PREVIEW_SAMPLE_VALUES,
  surveyEntries, surveyPreviewMarkup, surveyRowText, isNumericSurveyStyle, SURVEY_DEFAULT_QUESTIONS, surveyLabel,
} from '../constants/signaturePlaceholders';
import { BRAND_ASSET_TYPE_LABELS, BRAND_ASSET_INSERT_WIDTH } from '../constants/brandAssetTypes';
import { LANGUAGE_CHOICES, LANGUAGE_LABELS, LANGUAGE_ORDER } from '../constants/languages';
import useOverlayClose from '../hooks/useOverlayClose';
import GroupAssignmentPicker from './GroupAssignmentPicker';
import RichHtmlEditor from './RichHtmlEditor';
import '../styles/global.css';
import '../styles/variables.css';
import { useT, pick } from '../i18n';
import './SignatureForm.css';

const messages = {
  notInDirectory: { tr: 'dizinde bulunamadı', en: 'not in directory' },
  sampleJobTitle: { tr: 'Satış Müdürü', en: 'Sales Manager' },
  editTitle: { tr: 'Şablonu Düzenle', en: 'Edit Template' },
  createTitle: { tr: 'Yeni Şablon Oluştur', en: 'Create New Template' },
  templateName: { tr: 'Şablon Adı:', en: 'Template Name:' },
  survey: { tr: 'Memnuniyet Anketi', en: 'Satisfaction Survey' },
  surveyHint: {
    tr: () => <>İmzaya eklenen emoji veya yıldız puanlama anketi — bu şablonun atandığı grup(lar) görür, yanıtlar{' '}<a href="/surveys" target="_blank" rel="noreferrer">Memnuniyet Anketi</a> sayfasından takip edilir.</>,
    en: () => <>An emoji or star rating survey added to the signature — seen by the group(s) this template is assigned to; responses are tracked on the{' '}<a href="/surveys" target="_blank" rel="noreferrer">Satisfaction Survey</a> page.</>,
  },
  addSurvey: { tr: '🙂 Memnuniyet Anketi Ekle', en: '🙂 Add Satisfaction Survey' },
  remove: { tr: 'Kaldır', en: 'Remove' },
  surveyQuestion: { tr: 'Anket sorusu', en: 'Survey question' },
  surveyQuestionHint: {
    tr: 'Puanlama satırının üstünde görünür. Boş bırakırsanız şablon dilindeki varsayılan soru kullanılır.',
    en: 'Shown above the rating row. Leave it empty to use the default question in the template’s language.',
  },
  alarm: { tr: 'Olumsuz geri bildirim alarmı', en: 'Negative feedback alert' },
  off: { tr: 'Kapalı', en: 'Off' },
  andBelow: { tr: '{rating} ve altı ({label})', en: '{rating} and below ({label})' },
  alertEmailPlaceholder: { tr: 'bildirim@sirket.com', en: 'alerts@company.com' },
  alarmHint: {
    tr: () => <>Seçilen puan veya altında bir yanıt geldiğinde otomatik bir aksiyon kaydı açılır ve (e-posta girildiyse) bildirim gönderilir — bkz. <a href="/surveys" target="_blank" rel="noreferrer">Memnuniyet Anketi</a> sayfası.</>,
    en: () => <>When a response at or below the selected score comes in, an action record is opened automatically and (if an email is entered) a notification is sent — see the <a href="/surveys" target="_blank" rel="noreferrer">Satisfaction Survey</a> page.</>,
  },
  parameters: { tr: 'Parametreler', en: 'Parameters' },
  parametersHint: { tr: 'İmza içine eklemek için tıklayın', en: 'Click to insert into the signature' },
  htmlContent: { tr: 'HTML İçeriği:', en: 'HTML Content:' },
  fromLibrary: { tr: 'Marka Kütüphanesinden Ekle', en: 'Insert from Brand Library' },
  fromLibraryHint: { tr: 'Logo, ikon veya banner görsellerinizden birini imzaya ekleyin.', en: 'Add one of your logo, icon or banner images to the signature.' },
  noAssets: { tr: 'Henüz marka varlığı yüklenmemiş.', en: 'No brand assets uploaded yet.' },
  openAssets: { tr: 'Marka Varlıkları\'nı aç', en: 'Open Brand Assets' },
  languagesHint: {
    tr: 'Bu içeriği birden fazla dilde hazırlayabilirsiniz — aşağıdaki her sekmenin kendi içeriği vardır, birini varsayılan yapın. Taskpane\'de birden fazla dil varsa kullanıcı aralarında seçim yapar; hiç seçim yapmazsa varsayılan içerik kullanılır.',
    en: 'You can prepare this content in several languages — each tab below has its own content; make one the default. If there are several languages, users choose between them in the taskpane; without a choice, the default content is used.',
  },
  defaultSuffix: { tr: ' (Varsayılan)', en: ' (Default)' },
  makeDefault: { tr: 'Bunu Varsayılan Yap', en: 'Make Default' },
  removeLanguage: { tr: 'Bu Dili Kaldır', en: 'Remove This Language' },
  addLanguage: { tr: '+ Dil Ekle', en: '+ Add Language' },
  livePreview: { tr: 'Canlı Önizleme', en: 'Live Preview' },
  livePreviewHint: {
    tr: () => <>Tüm değişkenler örnek verilerle gösterilir; gerçek e-postada bu değerler kullanıcının kendi bilgileriyle değişir. Örnek değerleri düzenlemek için{' '}<a href="/settings?tab=placeholders" target="_blank" rel="noreferrer">buraya tıkla</a>.</>,
    en: () => <>All variables are shown with sample data; in a real email they’re replaced with the user’s own details. To edit the sample values,{' '}<a href="/settings?tab=placeholders" target="_blank" rel="noreferrer">click here</a>.</>,
  },
  groups: { tr: 'İlişkili Gruplar', en: 'Assigned Groups' },
  groupsHint: {
    tr: 'Bu şablonun uygulanacağı grupları seçin ve aynı kullanıcıda birden fazla şablon çakışırsa hangisinin öncelikli olacağını belirtin',
    en: 'Choose the groups this template applies to, and set which template wins when several apply to the same user',
  },
  personal: { tr: 'Kişiye Özel Atama', en: 'Direct Assignment' },
  personalHint: {
    tr: 'Bu şablonu grup üyeliğinden bağımsız olarak doğrudan belirli kişilere atayın. Kişiye özel atama grup atamalarından önce gelir; aynı kişi birden fazla şablona atanmışsa önceliği yüksek olan kullanılır. Yalnızca kişiye özel HTML imza ve kullanıcının İmzam\'da kendi yaptığı seçim bunun önüne geçer.',
    en: 'Assign this template directly to specific people, regardless of group membership. Direct assignments take precedence over group assignments; if a person is assigned to several templates, the one with the higher priority is used. Only a personal HTML signature and the user’s own choice on My Signature override it.',
  },
  userSearch: { tr: 'Ad veya e-posta ara', en: 'Search name or email' },
  userEmpty: { tr: 'Dizinde kullanıcı bulunamadı.', en: 'No users found in the directory.' },
  userNoMatch: { tr: 'Aramanızla eşleşen kullanıcı bulunamadı.', en: 'No users match your search.' },
};

// Renders form content only — no overlay/box of its own. Both call sites
// (Signatures.jsx's .modal-overlay/.modal-window, UserDetailPopup's
// .modal-overlay/.popup-box) already provide a modal shell; this used to
// render a second one nested inside theirs, which is why the modal looked
// broken (no real header bar, a floating × with no title).
export default function SignatureForm({
  onSave,
  onCancel,
  title,
  // Save error from the caller (e.g. the backend rejected the template) —
  // shown right above Save so it's seen inside the modal.
  error = null,
  groups = [],
  parameters = PERSONAL_PLACEHOLDERS,
  initialName = '',
  initialHtml = '',
  initialLanguage = 'tr',
  // [{language, html_content}, ...] — the template's NON-default language
  // variants (see backend SignatureLanguageVariant). Always empty for the
  // per-user override form (UserDetailPopup, allowSurvey=false) — overrides
  // have no language concept at all.
  initialLanguageVariants = [],
  initialAssignments = [],
  // Direct per-user assignments [{email, priority}] and the directory users to
  // pick from ([{email, name}]). The "Kişiye Özel Atama" section only renders
  // when `users` is passed — the per-user override form (UserDetailPopup)
  // doesn't pass it.
  initialUserAssignments = [],
  users,
  initialSurvey = null,
  // Surveys attach to a SignatureTemplate (see backend SurveyTemplate.
  // signature_template OneToOneField) — UserDetailPopup.jsx uses this same
  // form to edit a per-user UserSignatureOverride, which isn't a template
  // and can't carry a survey, so it opts out of the control entirely.
  allowSurvey = true,
  brandColors,
  brandAssets = [],
}) {
  const { t, lang } = useT(messages);
  const [name, setName] = useState(initialName);
  // Every configured language's content lives here, uniform in the UI
  // regardless of the backend's asymmetric default/variant storage split
  // (handleSubmit translates back to that shape on save):
  // { [languageCode]: { html: string, isDefault: boolean } } — always
  // exactly one isDefault:true entry. Which group actually gets this
  // template is still decided by ordinary group targeting below; the
  // DEFAULT language also drives the attached survey's public response-
  // page language (backend SignatureTemplate.language) — a taskpane user
  // who hasn't picked a language sees whichever variant is default here.
  const [variants, setVariants] = useState(() => {
    const obj = { [initialLanguage]: { html: initialHtml, isDefault: true } };
    (initialLanguageVariants || []).forEach(v => {
      obj[v.language] = { html: v.html_content, isDefault: false };
    });
    return obj;
  });
  const [activeLanguageTab, setActiveLanguageTab] = useState(initialLanguage);
  const [showAddLanguage, setShowAddLanguage] = useState(false);
  // Guards against editorRef.current.setContent(...) calls we make
  // ourselves (switching tabs, adding/removing a language) from being
  // mistaken for a real edit by RichHtmlEditor's onEditorChange — without
  // it, loading a different tab's content into the editor would overwrite
  // the OUTGOING tab's stored html with the incoming tab's content.
  const isProgrammaticEditorUpdate = useRef(false);
  // null = no survey on this template; 3 or 5 = the configured scale.
  // A deactivated (is_active: false) survey shouldn't reappear as
  // "configured" when the editor reopens — its history is preserved
  // server-side, but the admin has to explicitly re-add it here.
  const [surveyScale, setSurveyScale] = useState(
    initialSurvey && initialSurvey.is_active ? initialSurvey.scale : null
  );
  // 'emoji' or 'stars' — see SURVEY_OPTIONS. Only meaningful while a survey exists.
  const [surveyStyle, setSurveyStyle] = useState((initialSurvey && initialSurvey.style) || 'emoji');
  // '' = the default question in the template's language (SURVEY_DEFAULT_QUESTIONS).
  const [surveyQuestion, setSurveyQuestion] = useState((initialSurvey && initialSurvey.question) || '');
  // null = alarm disabled. A response rated at or below this triggers a
  // SurveyFeedbackAction + optional email (see backend
  // views._trigger_negative_feedback_alert). Compared against the raw
  // rating value, so it only makes sense relative to the scale above.
  const [alertThreshold, setAlertThreshold] = useState(
    initialSurvey && initialSurvey.alert_threshold != null ? initialSurvey.alert_threshold : ''
  );
  const [alertEmail, setAlertEmail] = useState(
    (initialSurvey && initialSurvey.alert_email) || ''
  );
  const [showSurveyPicker, setShowSurveyPicker] = useState(false);
  const [assignments, setAssignments] = useState(() => {
    // initialAssignments: [{group_name, priority}]
    if (!initialAssignments || !Array.isArray(initialAssignments)) return {};
    const obj = {};
    initialAssignments.forEach(a => {
      obj[a.group_name || a.group] = a.priority;
    });
    return obj;
  });
  const [userAssignments, setUserAssignments] = useState(() => {
    const obj = {};
    (initialUserAssignments || []).forEach(a => { obj[a.email.toLowerCase()] = a.priority; });
    return obj;
  });
  // Directory users + anyone already assigned who isn't in the directory
  // (left the company, directory not synced) so they can still be removed.
  const userOptions = React.useMemo(() => {
    const byEmail = new Map();
    (users || []).forEach(u => {
      const email = (u.email || '').toLowerCase();
      if (email) byEmail.set(email, { id: email, name: u.name || u.displayName || email, hint: email });
    });
    Object.keys(userAssignments).forEach(email => {
      if (!byEmail.has(email)) byEmail.set(email, { id: email, name: email, hint: t('notInDirectory') });
    });
    return [...byEmail.values()].sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [users, userAssignments, t, lang]);
  const toggleUser = email => {
    setUserAssignments(prev => {
      const copy = { ...prev };
      if (copy[email] != null) delete copy[email];
      else copy[email] = 1;
      return copy;
    });
  };
  const changeUserPriority = (email, val) => {
    setUserAssignments(prev => ({ ...prev, [email]: Number(val) }));
  };
  // Org-level admin-edited sample values (Settings > Placeholder) — override
  // PREVIEW_SAMPLE_VALUES' generic defaults for the personal tokens in the
  // live preview below. Best-effort: if this fails to load, the preview
  // just falls back to the generic samples, same as before this existed.
  const [orgPlaceholderOverrides, setOrgPlaceholderOverrides] = useState({});
  useEffect(() => {
    api.get(endpoints.signatures.previewPlaceholders)
      .then(data => setOrgPlaceholderOverrides(data.overrides || {}))
      .catch(() => {});
  }, []);

  const editorRef = useRef();

  // Fires on every real edit (typing, insertParam, paste, etc.) — writes
  // into the currently-active tab's stored html. Ignored when we ourselves
  // just called setContent() to load a different tab (see
  // isProgrammaticEditorUpdate above).
  const handleEditorChange = content => {
    if (isProgrammaticEditorUpdate.current) {
      isProgrammaticEditorUpdate.current = false;
      return;
    }
    setVariants(prev => ({ ...prev, [activeLanguageTab]: { ...prev[activeLanguageTab], html: content } }));
  };

  const switchLanguageTab = code => {
    if (code === activeLanguageTab || !variants[code]) return;
    if (editorRef.current) {
      isProgrammaticEditorUpdate.current = true;
      editorRef.current.setContent(variants[code].html || '');
    }
    setActiveLanguageTab(code);
  };

  const addLanguage = code => {
    if (editorRef.current) {
      isProgrammaticEditorUpdate.current = true;
      editorRef.current.setContent('');
    }
    setVariants(prev => ({ ...prev, [code]: { html: '', isDefault: false } }));
    setActiveLanguageTab(code);
    setShowAddLanguage(false);
  };

  // Purely local — no server call. Just moves the isDefault flag; each
  // language already keeps its own content under its own key, so there's
  // nothing to move/swap. Persisted (like everything else in this form)
  // only when Kaydet is actually pressed.
  const makeDefaultLanguage = code => {
    setVariants(prev => {
      const next = {};
      Object.entries(prev).forEach(([lang, data]) => {
        next[lang] = { ...data, isDefault: lang === code };
      });
      return next;
    });
  };

  const removeLanguage = code => {
    if (variants[code]?.isDefault) return; // no remove control on the default tab anyway
    const remaining = { ...variants };
    delete remaining[code];
    if (activeLanguageTab === code) {
      const fallback = Object.keys(remaining).find(l => remaining[l].isDefault) || Object.keys(remaining)[0];
      if (editorRef.current) {
        isProgrammaticEditorUpdate.current = true;
        editorRef.current.setContent(remaining[fallback]?.html || '');
      }
      setActiveLanguageTab(fallback);
    }
    setVariants(remaining);
  };

  const insertParam = ph => {
    if (editorRef.current) {
      editorRef.current.insertContent(ph);
      editorRef.current.focus();
    }
  };

  // Marka Kütüphanesinden Ekle — inserts the SPECIFIC clicked asset's real,
  // resolved URL. Deliberately no asset_type branch/dynamic-token special
  // case (a {{BrandLogo}}-style token that resolves to "whichever logo is
  // currently newest" would be dishonest here — it could silently insert a
  // different image than the one actually clicked whenever an org has more
  // than one logo asset). The {{BrandLogo}} chip that used to sit next to
  // this was removed 2026-09 once this picker existed — the token itself
  // still resolves server-side for any template that already has it saved.
  // Same insertContent shape as Campaigns.jsx's insertBrandAsset.
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const assetPickerOverlayClose = useOverlayClose(() => setShowAssetPicker(false));
  const ASSET_TYPE_RANK = { logo: 0, icon: 1, banner: 2, other: 3 };
  const insertBrandAsset = asset => {
    const width = BRAND_ASSET_INSERT_WIDTH[asset.asset_type] || BRAND_ASSET_INSERT_WIDTH.other;
    editorRef.current?.insertContent(`<img src="${asset.url}" alt="${asset.name}" width="${width}" />`);
    editorRef.current?.focus();
    setShowAssetPicker(false);
  };

  // Only inserts the {{Survey}} token the first time (editing an already-
  // configured survey just changes its scale, without duplicating the
  // token in the editor content).
  const handleConfirmSurvey = (scale, style) => {
    if (surveyScale === null) {
      insertParam('{{Survey}}');
    }
    // A threshold set for the previous scale may not exist as a rating on
    // the new one (e.g. 4 was valid on a 5-point scale, not on 3-point) —
    // reset it rather than silently keeping a value the new scale can't
    // represent.
    if (alertThreshold !== '' && !SURVEY_SCALES[scale].emojis.some(e => e.rating === Number(alertThreshold))) {
      setAlertThreshold('');
    }
    setSurveyScale(scale);
    setSurveyStyle(style);
    setShowSurveyPicker(false);
  };

  const handleRemoveSurvey = () => {
    setSurveyScale(null);
    // Strips the token from EVERY language's content, not just the active
    // tab's — otherwise switching away and back would still show it (and,
    // more importantly, it'd still be saved into the other variants).
    setVariants(prev => {
      const next = {};
      Object.entries(prev).forEach(([lang, data]) => {
        next[lang] = { ...data, html: data.html.split('{{Survey}}').join('') };
      });
      return next;
    });
    if (editorRef.current) {
      isProgrammaticEditorUpdate.current = true;
      editorRef.current.setContent((variants[activeLanguageTab]?.html || '').split('{{Survey}}').join(''));
    }
  };

  // Live preview: every insertable token is substituted with a sample value
  // (the org's own Settings > Placeholder overrides where set, otherwise
  // PREVIEW_SAMPLE_VALUES' generic defaults) so the preview always looks
  // like a finished signature rather than showing raw {{Token}} text or a
  // broken image icon for the photo/QR tokens. {{Survey}} is handled
  // separately since its markup depends on the admin's chosen scale (3 vs 5
  // emojis) rather than a fixed sample. None of this touches `variants`, the
  // actual saved template content — only what's rendered into this iframe.
  // Reads the ACTIVE tab's content, so switching language tabs also
  // switches which language's preview is shown. Must be kept in sync by
  // hand with the backend's actual output shape (computed_placeholders.py)
  // and SURVEY_SCALES' emoji sets.
  const templateDefaultLanguage =
    (Object.entries(variants).find(([, data]) => data.isDefault) || [activeLanguageTab])[0];
  const previewSrcDoc = React.useMemo(() => {
    // The backend renders the survey (question default, captions) in the
    // template's DEFAULT language whichever variant is sent — preview the same.
    const surveyMarkup = surveyScale
      ? surveyPreviewMarkup(surveyScale, surveyStyle, templateDefaultLanguage, surveyQuestion)
      : '';
    const activeHtml = variants[activeLanguageTab]?.html || '';
    let resolvedHtml = activeHtml.split('{{Survey}}').join(surveyMarkup);
    const samples = { ...PREVIEW_SAMPLE_VALUES, '{{JobTitle}}': t('sampleJobTitle') };
    Object.entries(samples).forEach(([token, sampleValue]) => {
      const value = orgPlaceholderOverrides[token] || sampleValue;
      resolvedHtml = resolvedHtml.split(token).join(value);
    });
    return `<html><body style="margin:0;padding:16px;font-family:Arial, Helvetica, sans-serif;font-size:14px;color:#1a1a1a;">${resolvedHtml}</body></html>`;
  }, [variants, activeLanguageTab, surveyScale, surveyStyle, surveyQuestion, templateDefaultLanguage, orgPlaceholderOverrides, t]);

  const toggleGroup = gid => {
    setAssignments(prev => {
      const copy = { ...prev };
      if (copy[gid] != null) delete copy[gid];
      else copy[gid] = 1;      // seçince önceliği 1 olarak başlat
      return copy;
    });
  };

  // öncelik input değişimi
  const changePriority = (gid, val) => {
    setAssignments(prev => ({
      ...prev,
      [gid]: Number(val)
    }));
  };

  // Bring a new save error into view (the form scrolls inside the modal).
  const errorRef = useRef(null);
  useEffect(() => {
    if (error && errorRef.current && errorRef.current.scrollIntoView) {
      errorRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [error]);

  const handleSubmit = e => {
    e.preventDefault();
    // The default language's content is the top-level html/language, same
    // shape the backend has always accepted — every OTHER configured
    // language goes into language_variants. Falls back to the active tab
    // if somehow nothing is flagged default (shouldn't happen — makeDefaultLanguage
    // always keeps exactly one true — but never leaves the form unsaveable).
    const defaultEntry = Object.entries(variants).find(([, data]) => data.isDefault)
      || [activeLanguageTab, variants[activeLanguageTab]];
    const [defaultLanguage, defaultData] = defaultEntry;
    const payload = {
      name,
      html: defaultData.html,
      language: defaultLanguage,
      language_variants: Object.entries(variants)
        .filter(([lang]) => lang !== defaultLanguage)
        .map(([lang, data]) => ({ language: lang, html_content: data.html })),
      assignments: Object.entries(assignments).map(
        ([group, priority]) => ({ group, priority })
      ),
      ...(users !== undefined ? {
        user_assignments: Object.entries(userAssignments).map(
          ([email, priority]) => ({ email, priority: priority || 1 })
        ),
      } : {}),
      survey: surveyScale
        ? {
            scale: surveyScale,
            style: surveyStyle,
            question: surveyQuestion.trim(),
            alert_threshold: alertThreshold === '' ? null : Number(alertThreshold),
            alert_email: alertEmail,
          }
        : null,
    };
    onSave(payload);
  };

  return (
    <div className="signature-form">
      <div className="signature-form-header">
        <h2>{title || (initialHtml ? t('editTitle') : t('createTitle'))}</h2>
        <button className="signature-form-close-btn" type="button" onClick={onCancel}>×</button>
      </div>
      <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('templateName')}</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                className='signature-name-input'
              />
            </div>

            {allowSurvey && (
              <div className="form-group">
                <label>{t('survey')}</label>
                <p className="form-group-hint">{t('surveyHint')}</p>
                {surveyScale === null ? (
                  <Button type="button" variant="secondary" onClick={() => setShowSurveyPicker(true)}>
                    {t('addSurvey')}
                  </Button>
                ) : (
                  <>
                    <div className="survey-config-row">
                      <Badge variant="info">
                        {pick(SURVEY_OPTIONS.find(o => o.scale === surveyScale && o.style === surveyStyle).label, undefined, lang)}
                        {': '}
                        <span className={surveyStyle === 'stars' ? 'survey-stars' : undefined}>
                          {surveyRowText(surveyScale, surveyStyle)}
                        </span>
                      </Badge>
                      <Button type="button" variant="secondary" onClick={() => setShowSurveyPicker(true)}>{t('edit')}</Button>
                      <Button type="button" variant="danger" onClick={handleRemoveSurvey}>{t('remove')}</Button>
                    </div>
                    <div className="survey-alert-config">
                      <label htmlFor="survey-question">{t('surveyQuestion')}</label>
                      <input
                        id="survey-question"
                        type="text"
                        maxLength={200}
                        className="signature-name-input"
                        value={surveyQuestion}
                        placeholder={SURVEY_DEFAULT_QUESTIONS[templateDefaultLanguage] || SURVEY_DEFAULT_QUESTIONS.tr}
                        onChange={e => setSurveyQuestion(e.target.value)}
                      />
                      <p className="form-group-hint">{t('surveyQuestionHint')}</p>
                    </div>
                    <div className="survey-alert-config">
                      <label htmlFor="survey-alert-threshold">{t('alarm')}</label>
                      <div className="survey-alert-config-row">
                        <select
                          id="survey-alert-threshold"
                          value={alertThreshold}
                          onChange={e => setAlertThreshold(e.target.value)}
                        >
                          <option value="">{t('off')}</option>
                          {[...surveyEntries(surveyScale, surveyStyle)].reverse().map(e => (
                            <option key={e.rating} value={e.rating}>
                              {t('andBelow', {
                                rating: e.rating,
                                label: isNumericSurveyStyle(surveyStyle) ? surveyLabel(e.label, lang) : `${e.emoji} ${surveyLabel(e.label, lang)}`,
                              })}
                            </option>
                          ))}
                        </select>
                        {alertThreshold !== '' && (
                          <input
                            type="email"
                            placeholder={t('alertEmailPlaceholder')}
                            value={alertEmail}
                            onChange={e => setAlertEmail(e.target.value)}
                            className="signature-name-input"
                          />
                        )}
                      </div>
                      <p className="form-group-hint">{t('alarmHint')}</p>
                    </div>
                  </>
                )}
                {showSurveyPicker && (
                  <div className="survey-scale-picker">
                    {SURVEY_OPTIONS.map(({ scale, style, label }) => (
                      <button
                        type="button"
                        key={`${scale}-${style}`}
                        className="survey-scale-option"
                        onClick={() => handleConfirmSurvey(scale, style)}
                      >
                        {isNumericSurveyStyle(style) ? (
                          // The exact e-mail markup, so the option looks like what recipients see.
                          <span
                            className="survey-scale-option-numeric"
                            dangerouslySetInnerHTML={{ __html: surveyPreviewMarkup(scale, style) }}
                          />
                        ) : (
                          <span className={`survey-scale-option-emojis ${style === 'stars' ? 'survey-stars' : ''}`.trim()}>
                            {surveyRowText(scale, style)}
                          </span>
                        )}
                        <span className="survey-scale-option-label">{pick(label, undefined, lang)}</span>
                      </button>
                    ))}
                    <button type="button" className="survey-scale-picker-cancel" onClick={() => setShowSurveyPicker(false)}>
                      {t('cancel')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 2. Parametreler — kişisel alanlar + hazır-markup chip'ler (profil
                fotoğrafı, vCard/QR) tek bir listede; ayrı bir "Kişiselleştirme"
                başlığı altında tutmanın kullanıcı için gerçek bir farkı yoktu,
                ikisi de aynı insertParam mekanizmasıyla tek tıkla ekleniyor. */}
            <div className="form-group">
              <label>{t('parameters')}</label>
              <p className="form-group-hint">{t('parametersHint')}</p>
              <div className="param-chip-list">
                {parameters.map(ph => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => insertParam(ph)}
                    className="param-chip"
                  >
                    {ph}
                  </button>
                ))}
                {PERSONALIZATION_CHIPS.map(chip => (
                  <button
                    key={chip.label}
                    type="button"
                    onClick={() => insertParam(chip.html)}
                    className="param-chip"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. İçerik — marka kütüphanesi butonu doğrudan bu başlığın
                altında, editörün hemen üstünde: eklenen görsel editöre gider,
                o yüzden tetikleyicisi editörle aynı bloktan doğal. */}
            <div className="form-group">
              <label>{t('htmlContent')}</label>
              <Button
                type="button" variant="secondary" className="signature-brand-library-btn"
                onClick={() => setShowAssetPicker(true)}
              >
                {t('fromLibrary')}
              </Button>
              <p className="form-group-hint">{t('fromLibraryHint')}</p>

              {showAssetPicker && (
                <div className="modal-overlay" {...assetPickerOverlayClose}>
                  <div className="modal-window signature-asset-picker-modal">
                    <h2>{t('fromLibrary')}</h2>
                    {brandAssets.length === 0 ? (
                      <p className="form-group-hint">
                        {t('noAssets')}{' '}
                        <a href="/brand-assets" target="_blank" rel="noreferrer">{t('openAssets')}</a>
                      </p>
                    ) : (
                      <div className="signature-asset-picker-grid">
                        {[...brandAssets]
                          .sort((a, b) => (ASSET_TYPE_RANK[a.asset_type] ?? 4) - (ASSET_TYPE_RANK[b.asset_type] ?? 4))
                          .map(asset => (
                            <button
                              type="button"
                              key={asset.id}
                              className="signature-asset-picker-item"
                              onClick={() => insertBrandAsset(asset)}
                            >
                              <img src={asset.url} alt={asset.name} />
                              <span>{asset.name}</span>
                              <Badge variant="info">{BRAND_ASSET_TYPE_LABELS[asset.asset_type] ? pick(BRAND_ASSET_TYPE_LABELS[asset.asset_type], undefined, lang) : asset.asset_type}</Badge>
                            </button>
                          ))}
                      </div>
                    )}
                    <div className="form-actions">
                      <Button type="button" variant="secondary" onClick={() => setShowAssetPicker(false)}>{t('close')}</Button>
                    </div>
                  </div>
                </div>
              )}

              {allowSurvey && (
                <p className="form-group-hint">{t('languagesHint')}</p>
              )}

              <div className="signature-content-editor">
                {allowSurvey && (
                  <div className="signature-language-tabs">
                    {LANGUAGE_ORDER.filter(code => variants[code]).map(code => (
                      <div key={code} className={`signature-language-tab${code === activeLanguageTab ? ' active' : ''}`}>
                        <button
                          type="button"
                          className="signature-language-tab-btn"
                          onClick={() => switchLanguageTab(code)}
                        >
                          {LANGUAGE_LABELS[code] || code}{variants[code].isDefault ? t('defaultSuffix') : ''}
                        </button>
                        {!variants[code].isDefault && (
                          <span className="signature-language-tab-actions">
                            <button
                              type="button"
                              title={t('makeDefault')}
                              onClick={() => makeDefaultLanguage(code)}
                            >★</button>
                            <button
                              type="button"
                              title={t('removeLanguage')}
                              onClick={() => removeLanguage(code)}
                            >×</button>
                          </span>
                        )}
                      </div>
                    ))}
                    {LANGUAGE_CHOICES.some(([code]) => !variants[code]) && (
                      <div className="signature-language-tab-add">
                        <button type="button" onClick={() => setShowAddLanguage(o => !o)}>{t('addLanguage')}</button>
                        {showAddLanguage && (
                          <div className="signature-language-add-menu">
                            {LANGUAGE_CHOICES.filter(([code]) => !variants[code]).map(([code, label]) => (
                              <button type="button" key={code} onClick={() => addLanguage(code)}>{label}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <RichHtmlEditor
                  onInit={(evt, editor) => { editorRef.current = editor; }}
                  initialValue={initialHtml}
                  onEditorChange={handleEditorChange}
                  height={360}
                  brandColors={brandColors}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('livePreview')}</label>
              <p className="form-group-hint">{t('livePreviewHint')}</p>
              <iframe
                title="signature-preview"
                className="signature-preview-frame"
                srcDoc={previewSrcDoc}
                sandbox=""
              />
            </div>

            {/* 4. İlişkili Gruplar + Öncelik */}
            <div className="form-group">
              <label>{t('groups')}</label>
              <p className="form-group-hint">{t('groupsHint')}</p>
              <GroupAssignmentPicker
                groups={groups}
                assignments={assignments}
                onToggleGroup={toggleGroup}
                onChangePriority={changePriority}
                showPriority
              />
            </div>

            {/* 4b. Kişiye özel atama */}
            {users !== undefined && (
              <div className="form-group">
                <label>{t('personal')}</label>
                <p className="form-group-hint">{t('personalHint')}</p>
                <GroupAssignmentPicker
                  groups={userOptions}
                  assignments={userAssignments}
                  onToggleGroup={toggleUser}
                  onChangePriority={changeUserPriority}
                  showPriority
                  searchPlaceholder={t('userSearch')}
                  emptyText={t('userEmpty')}
                  noMatchText={t('userNoMatch')}
                />
              </div>
            )}

        {error && (
          <div ref={errorRef} className="signature-form-error">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        {/* 5. Kaydet / İptal */}
        <div className="form-actions">
          <Button type="submit">{t('save')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel}>{t('cancel')}</Button>
        </div>
      </form>
    </div>
  );
}
